import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { filterByLabel } from "@/lib/filterByLabel";
import DropdownService from "@/services/APIs/DropdownService";
import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, Col, Form, Row, Select } from "antd";
import { Save } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export interface SLASchemaConfigurationValues {
  sla_policy_schema?: string;
  business_hours_schema?: string;
  holiday_calendar_schema?: string;
  escalation_time_schema?: string;
  notify_role_foreign_key_schema?: string;
}

const SCHEMA_TYPE_MAP: { field: keyof SLASchemaConfigurationValues; schema_type: string }[] = [
  { field: "sla_policy_schema", schema_type: "sla_policy" },
  { field: "business_hours_schema", schema_type: "business_hours" },
  { field: "holiday_calendar_schema", schema_type: "holiday_calendar" },
  { field: "escalation_time_schema", schema_type: "escalation_time" },
  { field: "notify_role_foreign_key_schema", schema_type: "applye_table" },
];

const SLASchemaConfigurationPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SLASchemaConfigurationValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: schemaOptions = [], isLoading: schemaLoading } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const schemas = SCHEMA_TYPE_MAP.filter(({ field }) => values[field]).map(
        ({ field, schema_type }) => ({
          schema_type,
          table_name: values[field] as string,
        })
      );

      await SLAConfigurationService.saveSLASchemaConfiguration({
        data_source_type: "FORMILY",
        schemas,
      });

      notify.success("SLA configuration saved.");
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ["sla-schema-configurations"] });
      navigate(navigationRoutes.SLA_CONFIGURATION);
    } catch (error: unknown) {
      if (error && typeof error === "object" && "errorFields" in error) {
        notify.error("Please fix validation errors.");
      } else {
        notify.error("Failed to save SLA configuration.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const borderedSectionClass =
    "rounded-lg border border-solid border-[rgba(0,0,0,0.06)] bg-white p-4";

  const schemaSelectProps = {
    allowClear: true,
    showSearch: true,
    filterOption: filterByLabel,
    loading: schemaLoading,
    disabled: isSubmitting,
    options: schemaOptions,
    className: "w-full",
  };

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title="SLA Configuration"
          onBack={() => navigate(navigationRoutes.SLA_CONFIGURATION)}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title="Save SLA Configuration"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form<SLASchemaConfigurationValues>
        form={form}
        layout="vertical"
      >
        <div className={`${borderedSectionClass} mb-4`}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="sla_policy_schema"
                label={<span className="font-semibold">SLA Policy Schema</span>}
                rules={[{ required: true, message: "Please select SLA policy schema" }]}
              >
                <Select {...schemaSelectProps} placeholder="Select Schema" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="business_hours_schema"
                label={<span className="font-semibold">Business Hours Schema</span>}
                rules={[{ required: true, message: "Please select business hours schema" }]}
              >
                <Select {...schemaSelectProps} placeholder="Select Schema" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="holiday_calendar_schema"
                label={<span className="font-semibold">Holiday Calendar Schema</span>}
                rules={[{ required: true, message: "Please select holiday calendar schema" }]}
              >
                <Select {...schemaSelectProps} placeholder="Select Schema" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="escalation_time_schema"
                label={<span className="font-semibold">Escalation Time Schema</span>}
                rules={[{ required: true, message: "Please select escalation time schema" }]}
              >
                <Select {...schemaSelectProps} placeholder="Select Schema" />
              </Form.Item>
            </Col>
          </Row>
          <div className="mb-2 mt-2 font-semibold">Main Apply Schema</div>
          <Form.Item
            name="notify_role_foreign_key_schema"
            label="Schema Name"
            rules={[{ required: true, message: "Please select Formily schema" }]}
          >
            <Select {...schemaSelectProps} placeholder="Select Formily Schema" />
          </Form.Item>
        </div>
      </Form>
    </Card>
  );
};

export default SLASchemaConfigurationPage;
